/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */


import {useCallback, useEffect, useRef, useState} from "react";
import {emptyQueryHistory, applyLayoutChange as applyLayoutChangeToState, detachFromSavedView as detachFromSavedViewInState, markRestored as markRestoredInState, QueryHistoryCandidate, QueryHistoryState, recordQuery as recordQueryInState, seedQueryHistory, touchCurrentEntry as touchCurrentEntryInState} from "qqq/models/query/QueryHistory";
import {readQueryHistory, updateQueryHistory} from "qqq/utils/qqq/QueryHistoryStorage";

export interface UseQueryHistoryResult
{
   state: QueryHistoryState;
   recordQuery: (candidate: QueryHistoryCandidate) => void;
   applyLayoutChange: (candidate: QueryHistoryCandidate) => void;
   touchCurrentEntry: (candidate: QueryHistoryCandidate) => void;
   markRestored: (entryId: string, restoredViewJson?: string) => void;
   detachFromSavedView: (entryId: string) => void;
   seedIfEmpty: (existingViewJson: string | null, savedViewId: number | null) => void;
}


/*******************************************************************************
 **
 *******************************************************************************/
function idSupplier(): string
{
   return (`${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`);
}


/*******************************************************************************
 ** hold the query history stored under a key (from buildQueryHistoryKey: one
 ** per user, table and table variant), and offer mutators that apply a change to
 ** the stored history and publish the result.  Every mutator swallows its own
 ** failures, so that history can never break the screen.
 **
 ** Storage is asynchronous, so changes are queued and applied one at a time, in
 ** the order they were made - after the first read, so the screen's first query
 ** on a page load is recorded against the stored history, not an empty one.
 **
 ** The entries are shared by every tab on the same key, but each tab (each use of
 ** this hook) keeps its own current entry: the one its screen is showing.  It
 ** starts as the stored one, since a reload shows the view that the tab which
 ** wrote last left behind.
 *******************************************************************************/
export default function useQueryHistory(historyKey: string | null, enabled: boolean): UseQueryHistoryResult
{
   const isActive = enabled && historyKey != null;
   const [state, setState] = useState<QueryHistoryState>(emptyQueryHistory);

   const currentEntryIdRef = useRef<string | null>(null);
   const queueRef = useRef<Promise<void>>(Promise.resolve());

   ///////////////////////////////////////////////////////////////////////////
   // bumped when the key changes (another table or variant, or the user id //
   // arriving after the first render) - queued work for an earlier key is  //
   // then skipped, and its results are not published.  The key it is for,  //
   // too, so a mutator from an earlier render (one that awaited something, //
   // say) called after the change does nothing.                            //
   ///////////////////////////////////////////////////////////////////////////
   const generationRef = useRef(0);
   const activeHistoryKeyRef = useRef<string | null>(null);


   /*******************************************************************************
    ** run a task after every task queued before it.
    *******************************************************************************/
   const enqueue = useCallback((task: () => Promise<void>) =>
   {
      queueRef.current = queueRef.current.then(task).catch((e) => console.log("Error in query history", e));
   }, []);


   /*******************************************************************************
    ** publish a history, as this tab sees it - with this tab's current entry.
    *******************************************************************************/
   const publish = useCallback((stored: QueryHistoryState) =>
   {
      const currentEntryId = stored.entries.some((entry) => entry.id === currentEntryIdRef.current) ? currentEntryIdRef.current : null;
      setState({...stored, currentEntryId: currentEntryId});
   }, []);


   useEffect(() =>
   {
      const generation = ++generationRef.current;
      activeHistoryKeyRef.current = isActive ? historyKey : null;
      currentEntryIdRef.current = null;
      setState(emptyQueryHistory());

      if (!isActive)
      {
         return;
      }

      enqueue(async () =>
      {
         const stored = await readQueryHistory(historyKey);
         if (generation === generationRef.current)
         {
            currentEntryIdRef.current = stored.currentEntryId;
            publish(stored);
         }
      });
   }, [historyKey, isActive]);


   /*******************************************************************************
    ** queue an updater against the stored history - it sees this tab's current
    ** entry as the current one - and publish what gets written.  If the write
    ** fails, publish what is stored instead.  An updater that changes nothing
    ** (returns the state it was given) writes nothing.
    *******************************************************************************/
   const update = useCallback((updater: (current: QueryHistoryState) => QueryHistoryState) =>
   {
      if (!isActive || historyKey !== activeHistoryKeyRef.current)
      {
         return;
      }

      const generation = generationRef.current;
      enqueue(async () =>
      {
         if (generation !== generationRef.current)
         {
            return;
         }

         try
         {
            let changed = false;
            const written = await updateQueryHistory(historyKey, (stored) =>
            {
               const asThisTabSeesIt = {...stored, currentEntryId: currentEntryIdRef.current};
               const updated = updater(asThisTabSeesIt);
               changed = (updated !== asThisTabSeesIt);
               return (changed ? updated : stored);
            });
            if (generation === generationRef.current)
            {
               ////////////////////////////////////////////////////////////////////////////
               // unchanged, what comes back is the stored history as is - whose current //
               // entry may be another tab's, so keep this tab's own                     //
               ////////////////////////////////////////////////////////////////////////////
               if (changed)
               {
                  currentEntryIdRef.current = written.currentEntryId;
               }
               publish(written);
            }
         }
         catch (e)
         {
            console.log("Error updating query history", e);
            const stored = await readQueryHistory(historyKey);
            if (generation === generationRef.current)
            {
               publish(stored);
            }
         }
      });
   }, [historyKey, isActive]);

   const recordQuery = useCallback((candidate: QueryHistoryCandidate) =>
   {
      update((current) => recordQueryInState(current, candidate, Date.now(), idSupplier));
   }, [update]);

   const applyLayoutChange = useCallback((candidate: QueryHistoryCandidate) =>
   {
      update((current) => applyLayoutChangeToState(current, candidate, Date.now()));
   }, [update]);

   const touchCurrentEntry = useCallback((candidate: QueryHistoryCandidate) =>
   {
      update((current) => touchCurrentEntryInState(current, candidate, Date.now()));
   }, [update]);

   const markRestored = useCallback((entryId: string, restoredViewJson?: string) =>
   {
      update((current) => markRestoredInState(current, entryId, Date.now(), restoredViewJson));
   }, [update]);

   const detachFromSavedView = useCallback((entryId: string) =>
   {
      update((current) => detachFromSavedViewInState(current, entryId));
   }, [update]);

   const seedIfEmpty = useCallback((existingViewJson: string | null, savedViewId: number | null) =>
   {
      if (existingViewJson == null)
      {
         return;
      }

      update((current) => seedQueryHistory(current, existingViewJson, savedViewId, Date.now(), idSupplier));
   }, [update]);

   return ({state, recordQuery, applyLayoutChange, touchCurrentEntry, markRestored, detachFromSavedView, seedIfEmpty});
}
